import { DynamicDialogInstance } from "primevue/dynamicdialogoptions";
import GeneratingProgressBar from "@/components/GeneratingProgressBar.vue";
import { h, ref } from "vue";
import { useDialog } from "primevue/usedialog";
import { EntityType } from "@/stores/entity-store";
import { useToast } from "primevue/usetoast";
import config from "@/config";
import { authentication } from "@/lib/integrations";
import { useOrganizationManager } from "./organization-manager";
import { useEditManager, useEditStore } from "./edit-manager";
import { EventSource } from "eventsource";

export const genreationDialog = ref<DynamicDialogInstance>();

export const useGenerateDescriptionDialog = () => {
    const dialog = useDialog();
    const toast = useToast();
    const { organization } = useOrganizationManager();
    const { isEditing } = useEditManager();
    const { startEditing } = useEditStore();

    /**
     * Complete description
     */
    const generateDescription = async (
        entityId: string,
        entityType: EntityType,
        updateContent: (content: string) => Promise<void>
    ) => {
        const token = await authentication.getToken();
        const eventSource = new EventSource(
            `${config.bekendURI}/biz/entities/${entityType}/${entityId}/generate_description`,
            {
                fetch: (input, init) => {
                    return fetch(input, {
                        ...init,
                        method: "POST",
                        headers: {
                            ...init?.headers,
                            Authorization: `Bearer ${token}`,
                            Organization: organization?.value?.identity ?? "",
                        },
                    });
                },
            }
        );
        let content = "";

        eventSource.addEventListener("open", () => {
            genreationDialog.value?.close();
        });

        eventSource.addEventListener("message", async ({ data }) => {
            try {
                const json = JSON.parse(data);
                content += json.content;
                await updateContent(content);
            } catch {
                // Not regular message with content.
                toast.add({
                    severity: "error",
                    summary: `Got invalid information to continue ${entityType} description.`,
                });
            }
        });

        return await new Promise<void>((resolve, reject) => {
            eventSource.addEventListener("close", () => {
                eventSource.close();
                resolve();
            });
            eventSource.addEventListener("error", (error) => {
                toast.add({
                    severity: "error",
                    summary: `Could not complete ${entityType} description`,
                    detail: `${error.code}: ${error.message}`,
                });
                eventSource.close();
                reject(error);
            });
        });
    };

    const open = () => {
        genreationDialog.value = dialog.open(
            h(GeneratingProgressBar, {
                message: "Generating first draft, this may take a minute...",
            }),
            {
                props: {
                    header: "Generate a kickstart",
                    style: {
                        maxWidth: "600px",
                        width: "80%",
                    },
                    modal: true,
                    maximizable: false,
                    draggable: false,
                },
            }
        );
    };

    const generateContent = async (
        entityId: string,
        entityType: EntityType,
        updateContent?: (
            content: string | Blob,
            silent?: boolean
        ) => Promise<void>
    ) => {
        if (!updateContent) return;
        if (!isEditing.value) {
            await startEditing();
        }
        open();
        await generateDescription(entityId, entityType, updateContent);
    };

    return { generateContent };
};
